import { useEffect, useState } from 'react';

import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';

import { useAuth } from '@client/context/auth/AuthContext';
import useDocumentDataFetch from '@client/helpers/firebase/firestore/useFirestoreDocFetch';
import { db } from '@config/firebase';
import { type IntakeUserInfoBody } from '@customTypes/intake';
import {
  DEFAULT_ONBOARDING_INFO,
  type OnboardingContextType,
  type OnboardingInfo,
} from '@customTypes/onboarding';
import { type CompanyInfo } from '@customTypes/userInfo';
import { type NestedPartial } from '@customTypes/utilityTypes';
import { convertObjectToDotNotatedObjectProperties } from '@helpers/dataTransformation/convertObjectToDotNotatedObject';

const mergeOnboardingData = (
  defaultData: OnboardingContextType,
  fetchedData: Partial<OnboardingContextType>,
): OnboardingContextType => ({
  ...defaultData,
  isVisible: !!fetchedData.isVisible,
  ...fetchedData,
  intakeForm: { ...defaultData.intakeForm, ...fetchedData.intakeForm },
  proxyContract: {
    ...defaultData.proxyContract,
    ...fetchedData.proxyContract,
  },
  mandate: {
    ...defaultData.mandate,
    ...fetchedData.mandate,
  },
  ponto: { ...defaultData.ponto, ...fetchedData.ponto },
  activation: { ...defaultData.activation, ...fetchedData.activation },
});

export const useOnboardingData = () => {
  const { user } = useAuth();
  const [onboardingInfo, setOnboardingInfo] = useState<OnboardingInfo>({
    ...DEFAULT_ONBOARDING_INFO,
    isVisible: false,
  });
  const [userDocInfo, setUserDocInfo] = useState<null | IntakeUserInfoBody>(null);
  const companyInfoRef = doc(db, `users/${user?.uid}/userInfo`, 'companyInfo');
  const { data: companyInfo, loading: companyInfoLoading } =
    useDocumentDataFetch<CompanyInfo>(companyInfoRef);
  const { isVisible } = onboardingInfo;
  const [loading, setLoading] = useState(true);

  const onboardingDoc = doc(db, `users/${user?.uid}/userInfo`, 'onboardingInfo');
  const userDocInfoRef = doc(db, `users/${user?.uid}`);

  const isCompany =
    companyInfo?.type === 'bv' || companyInfo?.type === 'vof' || companyInfo?.type === 'commV';

  const intakeCorporationNotCompleted =
    userDocInfo?.incorporation?.isCompleted === false ? true : false;
  const isIntakeDoneByAstro = userDocInfo?.incorporation?.incorporationPartner === 'astro';
  // Using snapshot listener
  useEffect(() => {
    const unsubscribe = onSnapshot(onboardingDoc, docSnapshot => {
      try {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as OnboardingInfo;
          const mergedData = mergeOnboardingData(DEFAULT_ONBOARDING_INFO, data);

          setOnboardingInfo(mergedData);
        }
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
      } finally {
        setLoading(false);
      }
    });

    // Second subscription for userDocInfoRef
    const userDocInfoUnsubscribe = onSnapshot(userDocInfoRef, docSnapshot => {
      try {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as IntakeUserInfoBody;
          setUserDocInfo(data);
        }
      } catch (error) {
        console.error('Error fetching user doc data:', error);
      }
    });

    return () => {
      unsubscribe();
      userDocInfoUnsubscribe();
    };
  }, []);
  /**
   * Update partial onboarding info document in firestore using dot notation to update nested properties
   *
   * @param data - Partial<OnboardingInfo>
   * @returns Promise<void>
   * @example updatePartialOnboardingInfoDocument({ status: "completed" }) // updates status property in onboardingInfo document
   * @example updatePartialOnboardingInfoDocument({ intakeForm: { formStatus: "completed" } }) // updates intakeForm.formStatus property in onboardingInfo document
   * @example updatePartialOnboardingInfoDocument({ intakeForm: { formStatus: "completed" }, status: "completed" }) // updates intakeForm.formStatus and status property in onboardingInfo document
   * @example updatePartialOnboardingInfoDocument({ intakeForm: { formStatus: "completed" }, status: "completed", proxyContract: { formStatus: "completed" } }) // updates intakeForm.formStatus, status and proxyContract.formStatus property in onboardingInfo document
   */
  const updatePartialOnboardingInfoDocument = async (data: NestedPartial<OnboardingInfo>) => {
    const dataToUpdate = {
      ...data,
      lastUpdatedAt: serverTimestamp(),
    };
    // convert object to dot notation
    const dotNotatedData = convertObjectToDotNotatedObjectProperties({
      ...dataToUpdate,
    });
    // console.log("updating data...", dotNotatedData);
    await updateDoc(onboardingDoc, dotNotatedData);
  };
  // console.log("from useOnboardingData", onboardingInfo);
  return {
    onboardingInfo,
    userDocInfo,
    loading,
    companyInfo,
    companyInfoLoading,
    updatePartialOnboardingInfoDocument,
    isCompany: !!isCompany,
    isVisible: !!isVisible,
    intakeCorporationNotCompleted: !!intakeCorporationNotCompleted,
    isIntakeDoneByAstro,
  };
};
